import { SelectProps } from '@mantine/core';
import { Select } from 'src/design/atoms/Select';
import React from 'react';
import { useAuth } from 'src/hooks/useAuth';
import { useOrganization } from 'src/hooks/useOrganization';

export interface ICostCodeSelectorProps extends Omit<SelectProps, 'data'> {
  value: string | null;
  onChange: (value: string | null) => void;
  officeId?: number | null;
}

export const CostCodeSelector: React.FC<ICostCodeSelectorProps> = ({
  value,
  onChange,
  label = 'Cost Code',
  placeholder = 'Select a cost code',
  description = 'Only global cost codes or cost codes that are associated with your offices are shown.',
  officeId,
  ...props
}) => {
  const { user } = useAuth();
  const { costCodes, costCodesLoading } = useOrganization();

  // Prepare cost code options for the selector
  // Get user's office IDs from userOrganizationOffice
  const userOfficeIds = React.useMemo(() => {
    return (user?.userOrganizationOffice || [])
      .map((uo) => uo.organizationOfficeId)
      .filter((id): id is number => id !== undefined);
  }, [user?.userOrganizationOffice]);

  const costCodeOptions = React.useMemo(() => {
    return costCodes
      .filter((costCode) => {
        const hasNoOffice = !costCode.offices || costCode.offices.length === 0;

        // Global cost codes (no offices) are always shown
        if (hasNoOffice) {
          return true;
        }

        // Always filter by user's offices
        const matchesUserOffice = userOfficeIds.some((userOfficeId) =>
          costCode.offices?.some((c) => c.id === userOfficeId),
        );

        if (!matchesUserOffice) {
          return false;
        }

        // If officeId is provided, also filter by that specific office
        if (officeId !== undefined && officeId !== null) {
          return costCode.offices?.some((c) => c.id === officeId);
        }

        return true;
      })
      .map((costCode) => ({
        value: costCode.id,
        label: `${costCode.code} - ${costCode.name}`,
      }));
  }, [costCodes, userOfficeIds, officeId]);

  return (
    <Select
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      data={costCodeOptions}
      disabled={costCodesLoading}
      clearable
      searchable
      description={description}
      {...props}
    />
  );
};
